import { REGISTRY_BLOCK, type LayoutBlock } from '@web-studio/shared-schemas';
import { pickBlockField } from '../../lib/localized';

/** Hard cap for operator-managed gallery slots in Zone 4. */
export const IMAGE_GALLERY_MAX_FRAMES = 12;

export type GalleryArrangement = 'mosaic' | 'masonry' | 'filmstrip' | 'spotlight' | 'ledger';

export function imageGalleryMediaKey(index: number): string {
  return `frame${index}`;
}

export function imageGalleryCaptionKey(index: number): string {
  return `caption${index}`;
}

function isImageGalleryMediaKey(key: string): boolean {
  return /^frame\d+$/.test(key);
}

function isImageGalleryCaptionKey(key: string): boolean {
  return /^caption\d+$/.test(key);
}

/** Highest 1-based frame index present on media or caption content (0 when empty). */
export function resolveGalleryFrameCount(block: Pick<LayoutBlock, 'content' | 'media'>): number {
  let max = 0;
  for (const key of Object.keys(block.media ?? {})) {
    const match = /^frame(\d+)$/.exec(key);
    if (match) {
      max = Math.max(max, Number(match[1]));
    }
  }
  for (const key of Object.keys(block.content ?? {})) {
    const match = /^caption(\d+)$/.exec(key);
    if (match) {
      max = Math.max(max, Number(match[1]));
    }
  }
  if (!Number.isFinite(max) || max < 0) {
    return 0;
  }
  return Math.min(IMAGE_GALLERY_MAX_FRAMES, max);
}

export function resolveGalleryArrangement(registryBlockKey?: string | null): GalleryArrangement {
  switch (registryBlockKey) {
    case REGISTRY_BLOCK.presentation.imageGalleryFilmstrip:
      return 'filmstrip';
    case REGISTRY_BLOCK.presentation.imageGallerySpotlight:
      return 'spotlight';
    case REGISTRY_BLOCK.presentation.imageGalleryLedger:
      return 'ledger';
    case REGISTRY_BLOCK.presentation.imageGalleryMasonry:
      return 'masonry';
    default:
      return 'mosaic';
  }
}

export function galleryArrangementClass(arrangement: GalleryArrangement): string {
  return `ws-gallery--${arrangement}`;
}

export interface GalleryFrameSlot {
  /** 1-based slot, matches `frame1` / `caption1`. */
  index: number;
  mediaKey: string;
  captionKey: string;
  rawUrl?: string;
  caption: string;
}

export function readGalleryFrameSlots(
  block: Pick<LayoutBlock, 'content' | 'media'>,
  locale: string,
  options?: { includeEmpty?: boolean },
): GalleryFrameSlot[] {
  const includeEmpty = options?.includeEmpty ?? false;
  const count = resolveGalleryFrameCount(block);
  const slots: GalleryFrameSlot[] = [];

  for (let index = 1; index <= count; index += 1) {
    const mediaKey = imageGalleryMediaKey(index);
    const captionKey = imageGalleryCaptionKey(index);
    const raw = block.media?.[mediaKey];
    const rawUrl = typeof raw === 'string' && raw.length > 0 ? raw : undefined;
    const caption = pickBlockField(block.content ?? {}, captionKey, locale).trim();
    if (!includeEmpty && !rawUrl) {
      continue;
    }
    slots.push({ index, mediaKey, captionKey, rawUrl, caption });
  }

  return slots;
}

export type GalleryFrameWriteSlot = {
  /** Media URL; omit or empty removes the image. */
  url?: string | null;
  /** Caption map for every locale that should persist (typically all project locales). */
  captions: Record<string, string>;
};

/**
 * Rewrites gallery slots to contiguous `frame1…N` / `caption1…N`.
 * Drops prior frame/caption keys outside the new list.
 */
export function rewriteGalleryFrameSlots(
  block: LayoutBlock,
  slots: readonly GalleryFrameWriteSlot[],
): LayoutBlock {
  const nextMedia: NonNullable<LayoutBlock['media']> = { ...(block.media ?? {}) };
  const nextContent: LayoutBlock['content'] = { ...block.content };

  for (const key of Object.keys(nextMedia)) {
    if (isImageGalleryMediaKey(key)) {
      delete nextMedia[key];
    }
  }
  for (const key of Object.keys(nextContent)) {
    if (isImageGalleryCaptionKey(key)) {
      delete nextContent[key];
    }
  }

  const limited = slots.slice(0, IMAGE_GALLERY_MAX_FRAMES);
  limited.forEach((slot, position) => {
    const index = position + 1;
    const mediaKey = imageGalleryMediaKey(index);
    const captionKey = imageGalleryCaptionKey(index);
    const url = typeof slot.url === 'string' ? slot.url.trim() : '';
    if (url) {
      nextMedia[mediaKey] = url;
    }
    const captions: Record<string, string> = {};
    for (const [locale, value] of Object.entries(slot.captions)) {
      captions[locale] = value;
    }
    nextContent[captionKey] = captions;
  });

  return {
    ...block,
    media: nextMedia,
    content: nextContent,
  };
}
